import {
  watchPresetOf,
  type NotifyOutcome,
  type WatchTargetType,
} from "@shared/types/notifications";

/**
 * The words of a personal Watch of one connection or schedule (alert routing, design "UI"; owner
 * 2026-09-28). One person, email only: failures and warnings by default, "works again" always,
 * "Every result" opt-in. Nobody can watch for someone else.
 *
 * Round 10 (C4): the editors' Watch button became `WatchBadge`, the one Watch control of the
 * Connections grid, the Schedules list and both editors; this file keeps the sentences it says.
 */

/** The target's noun in a sentence or an accessible name. */
export const WATCH_NOUN = {
  dataflow: "connection",
  schedule: "schedule",
} as const satisfies Record<WatchTargetType, string>;

/** "You get an email at a•••@x when this connection fails or has warnings, and when it works again." */
export function watchDescription(
  targetType: WatchTargetType,
  maskedEmail: string,
  every: boolean,
): string {
  const noun = WATCH_NOUN[targetType];
  return every
    ? `You get an email at ${maskedEmail} after every run of this ${noun}.`
    : `You get an email at ${maskedEmail} when this ${noun} fails or has warnings, and when it works again.`;
}

/**
 * A Watch of a custom set of outcomes (only the API can make one): the sentence names the set
 * itself, as My alerts does, never the failures-and-warnings sentence.
 */
export function watchOutcomesDescription(
  targetType: WatchTargetType,
  maskedEmail: string,
  outcomes: readonly NotifyOutcome[],
): string {
  const preset = watchPresetOf(outcomes);
  if (preset !== "custom") return watchDescription(targetType, maskedEmail, preset === "every");
  const noun = WATCH_NOUN[targetType];
  const words = (
    [
      ["failure", "failures"],
      ["warning", "warnings"],
      ["success", "successes"],
    ] as const
  )
    .filter(([outcome]) => outcomes.includes(outcome))
    .map(([, word]) => word);
  const list = words.length > 1 ? `${words.slice(0, -1).join(", ")} and ${words.at(-1)}` : words[0];
  return `You get an email at ${maskedEmail} for the ${list} of this ${noun}.`;
}

/** What a person who is not watching reads: about the Watch only, and how to start one. */
export function notWatchingDescription(targetType: WatchTargetType): string {
  const noun = WATCH_NOUN[targetType];
  return `You do not watch this ${noun}. Choose Watch, then Failures and warnings or Every result, to get an email about it.`;
}

/**
 * The first Watch's confirmation (round 4 item 3): what is covered, what is not, and where to
 * change it. The owner watched a connection, saw a successful run send nothing, and nothing on
 * screen had said that a success is not covered.
 */
export function watchConfirmation(targetType: WatchTargetType, maskedEmail: string): string {
  return `${watchDescription(targetType, maskedEmail, false)} A routine successful run sends nothing. To hear about every run, open Watching and choose Every result.`;
}
